import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { KeyRound, Lock, Eye, EyeOff, Loader2, CheckCircle2, Sparkles, AlertCircle } from "lucide-react";

/**
 * Public deep-link page for password reset. Guest lands here from the
 * admin-shared URL (?token=…), we verify the token, then let them set a
 * new password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [state, setState] = useState({ phase: "verifying", email: "", err: "" });
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ phase: "invalid", email: "", err: "Missing reset token." });
      return;
    }
    axios.post(`${API}/auth/verify-reset-token`, { token })
      .then(({ data }) => setState({ phase: "ready", email: data.email, err: "" }))
      .catch((e) => setState({ phase: "invalid", email: "", err: formatApiError(e.response?.data?.detail) || e.message }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (newPw !== confirmPw) { setErr("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPw });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Sparkles className="w-5 h-5 text-rose-700" strokeWidth={1.8} />
          <span className="font-heading text-base font-semibold">Kahani by Rakesh Janardan</span>
        </div>

        <div className="card p-6 bg-white" data-testid="reset-password-page">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h1 className="font-heading text-xl font-semibold">Set a new password</h1>
          </div>

          {state.phase === "verifying" && (
            <div className="text-sm text-stone-500 py-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verifying your link…</div>
          )}

          {state.phase === "invalid" && (
            <div className="py-4">
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span data-testid="reset-invalid">{state.err || "This link is invalid or has expired."}</span>
              </div>
              <button className="btn btn-outline btn-sm mt-4" onClick={() => navigate("/login")}>Go to sign in</button>
            </div>
          )}

          {state.phase === "ready" && !done && (
            <form onSubmit={submit} className="space-y-3">
              <div className="text-sm text-stone-600">Setting a new password for <span className="font-semibold text-stone-800" data-testid="reset-email">{state.email}</span>.</div>
              <PwField label="New password" value={newPw} setValue={setNewPw} show={showPw} setShow={setShowPw} testId="reset-new-password" />
              <PwField label="Confirm new password" value={confirmPw} setValue={setConfirmPw} show={showPw} setShow={setShowPw} testId="reset-confirm-password" />
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">{err}</div>}
              <button type="submit" className="btn btn-primary w-full" disabled={submitting} data-testid="reset-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" strokeWidth={1.8} />}
                Update password
              </button>
            </form>
          )}

          {done && (
            <div className="text-center py-4" data-testid="reset-done">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-700" strokeWidth={1.6} />
              </div>
              <div className="font-heading font-semibold">Password updated</div>
              <div className="text-sm text-stone-500 mt-1">Sign in with your new password for {state.email}.</div>
              <button className="btn btn-primary btn-sm mt-4" onClick={() => navigate("/login")} data-testid="reset-goto-login">Go to sign in</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PwField({ label, value, setValue, show, setShow, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <Lock className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
        <input required type={show ? "text" : "password"} className="input pl-9 pr-9" value={value} onChange={(e) => setValue(e.target.value)} data-testid={testId} minLength={6} />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700" tabIndex={-1}>
          {show ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
        </button>
      </div>
    </div>
  );
}
